import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, Text, Pressable, Animated, Easing, ScrollView } from "react-native";
import { X } from "@phosphor-icons/react";
import AxioInput from "../../axio-ds/Input/AxioInput";
import AxioButton from "../../axio-ds/Button/AxioButton";
import ThreadCard, { ThreadCardProps, ThreadType } from "../ThreadCard/ThreadCard";
import { colors, spacing, webTransitions } from "../../../styles/design-system";

const DRAWER_WIDTH = 360;

export interface WorkspaceThread extends ThreadCardProps {
  id: string;
}

export interface WorkspaceThreadsDrawerProps {
  visible: boolean;
  workspaceName: string;
  onClose: () => void;
  drawerWidth?: number;

  searchValue: string;
  onChangeSearch: (value: string) => void;

  onCreateThread: () => void;

  taskThreads: WorkspaceThread[];
  generalThreads: WorkspaceThread[];

  onSelectThread?: (id: string) => void;
  onPinToggle?: (id: string) => void;
  onDeleteThread?: (id: string) => void;
}

const WorkspaceThreadsDrawer: React.FC<WorkspaceThreadsDrawerProps> = ({
  visible,
  workspaceName,
  onClose,
  drawerWidth,
  searchValue,
  onChangeSearch,
  onCreateThread,
  taskThreads,
  generalThreads,
  onSelectThread,
  onPinToggle,
  onDeleteThread,
}) => {
  const [isMounted, setIsMounted] = useState(visible);
  const resolvedDrawerWidth = drawerWidth ?? DRAWER_WIDTH;
  const translateX = useRef(new Animated.Value(visible ? 0 : -resolvedDrawerWidth)).current;

  useEffect(() => {
    if (visible) {
      setIsMounted(true);
      Animated.timing(translateX, {
        toValue: 0,
        duration: 300,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(translateX, {
        toValue: -resolvedDrawerWidth,
        duration: 300,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          setIsMounted(false);
        }
      });
    }
  }, [resolvedDrawerWidth, translateX, visible]);

  if (!isMounted) return null;

  const handleSelect = (id: string) => {
    if (onSelectThread) {
      onSelectThread(id);
    }
  };

  const handlePinToggle = (id: string) => {
    if (onPinToggle) {
      onPinToggle(id);
    }
  };

  const handleDelete = (id: string) => {
    if (onDeleteThread) {
      onDeleteThread(id);
    }
  };

  const renderThreadList = (threads: WorkspaceThread[], type: ThreadType) =>
    threads.map((thread) => (
      <ThreadCard
        key={thread.id}
        {...thread}
        type={thread.type ?? type}
        onPress={() => handleSelect(thread.id)}
        onPinToggle={() => handlePinToggle(thread.id)}
        onDelete={() => handleDelete(thread.id)}
      />
    ));

  return (
    <Animated.View style={[styles.container, { width: resolvedDrawerWidth, transform: [{ translateX }] }]}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle} aria-level="2" role="heading">
          {workspaceName} Threads
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={onClose}
          style={styles.closeButton}
          accessibilityLabel="Close threads drawer">
          <X size={18} color={colors.grey[500]} alt="Close threads drawer" role="img" />
        </Pressable>
      </View>

      {/* Search + create */}
      <View style={styles.searchSection}>
        <AxioInput placeholder="Search threads..." value={searchValue} onChangeText={onChangeSearch} />
        <AxioButton
          label="+ Thread"
          variant="primary"
          size="md"
          onPress={onCreateThread}
          style={styles.createButton}
          accessibilityLabel="Create new thread"
        />
      </View>

      {/* Lists */}
      <ScrollView style={styles.listSection} contentContainerStyle={styles.listContent} showsVerticalScrollIndicator>
        {taskThreads.length > 0 && (
          <View style={styles.listGroup}>
            <Text style={styles.listLabel}>Task Threads</Text>
            {renderThreadList(taskThreads, "task")}
          </View>
        )}
        {generalThreads.length > 0 && (
          <View style={styles.listGroup}>
            <Text style={styles.listLabel}>General Threads</Text>
            {renderThreadList(generalThreads, "general")}
          </View>
        )}
      </ScrollView>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
    ...webTransitions.fast,
    zIndex: 10,
    flex: 1,
    height: "100%",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.md,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.grey[900],
  },
  closeButton: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  searchSection: {
    gap: spacing.xs,
    marginBottom: spacing.lg,
  },
  createButton: {
    alignSelf: "stretch",
  },
  listSection: {
    flex: 1,
  },
  listContent: {
    paddingBottom: spacing.lg,
  },
  listGroup: {
    marginBottom: spacing.lg,
  },
  listLabel: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    color: colors.grey[500],
    marginBottom: spacing.xs,
  },
});

export default WorkspaceThreadsDrawer;
